import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import millify from 'millify';
import { faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { FadeAnimation } from '@components/basics/Animation/FadeAnimation';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { TabContent, TabList, Tabs, TabTrigger } from '@components/main-components/tabs/Tab';
import { UserRow } from '@components/modals/shared/UserList/UserRow';
import { useModal } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import {
  FollowersFollowingModalFragmentDoc,
  FollowersModalRelationFragmentDoc,
  FollowingModalRelationFragmentDoc,
  getFragment,
} from '@graphql/reactQuery';
import { getSingularOrPlural } from '@utils/helpers';
import { EmptyUserStat } from '../EmptyState';
import {
  FOLLOWERS_EMPTY_HEADER,
  FOLLOWERS_EMPTY_SUBTEXT,
  FOLLOWING_EMPTY_HEADER,
  FOLLOWING_EMPTY_SUBTEXT,
} from '../shared';
import { useFollowersFollowing } from '../store';
import { List } from './List';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FollowingModal on User {
    id
    publicAddress
    followingCount
    username
  }
  fragment FollowingModalRelation on UserRelation {
    id
    userB {
      id
      ...UserModalRow
    }
  }

  fragment FollowersFollowingModal on User {
    id
    publicAddress
    followerCount
    followingCount
    username
  }
`);

export const FollowersFollowingModal = ({
  followData,
}: {
  followData: FragmentType<FollowersFollowingModalFragmentDoc>;
}) => {
  const { publicAddress, followerCount, followingCount, username } = getFragment(
    FollowersFollowingModalFragmentDoc,
    followData,
  );

  const {
    followerUsersData,
    followersLoading,
    followersLoadMore,
    followingUsersData,
    followingLoading,
    followingLoadMore,
  } = useFollowersFollowing({
    publicAddress,
  });
  const { closeCancelModal } = useModal();

  const followersLabel = `${followerCount ? `${millify(followerCount)} ` : ''}${getSingularOrPlural(
    {
      count: followerCount,
      word: 'follower',
    },
  )}`;
  const followingLabel = `${followingCount ? `${millify(followingCount)} ` : ''}following`;

  const renderFollower = (i: number, user: FragmentType<FollowersModalRelationFragmentDoc>) => {
    const { userA } = getFragment(FollowersModalRelationFragmentDoc, user);
    return (
      <FadeAnimation key={`user-${userA.id}-${i}`} className="px-6" transition={{ duration: 0.1 }}>
        <UserRow user={userA} sourceLocation="Followers Modal" />
      </FadeAnimation>
    );
  };

  const renderFollowing = (i: number, user: FragmentType<FollowingModalRelationFragmentDoc>) => {
    const { userB } = getFragment(FollowingModalRelationFragmentDoc, user);
    return (
      <FadeAnimation key={`user-${userB.id}-${i}`} className="px-6" transition={{ duration: 0.1 }}>
        <UserRow user={userB} sourceLocation="Following Modal" />
      </FadeAnimation>
    );
  };

  const body = (
    <>
      <TabContent value="followers">
        <List
          data={followerUsersData}
          loading={followersLoading}
          loadMore={followersLoadMore}
          onAtTopChange={() => null}
          onAtBottomChange={() => null}
          emptyComponent={
            <EmptyUserStat
              header={FOLLOWERS_EMPTY_HEADER}
              subText={FOLLOWERS_EMPTY_SUBTEXT(username)}
            />
          }
          renderItem={renderFollower}
        />
      </TabContent>
      <TabContent value="following">
        <List
          data={followingUsersData}
          loading={followingLoading}
          loadMore={followingLoadMore}
          onAtTopChange={() => null}
          onAtBottomChange={() => null}
          emptyComponent={
            <EmptyUserStat
              header={FOLLOWING_EMPTY_HEADER(username)}
              subText={FOLLOWING_EMPTY_SUBTEXT(username)}
            />
          }
          renderItem={renderFollowing}
        />
      </TabContent>
    </>
  );

  return (
    <Tabs defaultValue="followers">
      <StandardModal
        bodyClassName="p-0"
        containerClassName="pb-0"
        subHeader={
          <div className="flex flex-1 items-center justify-between pb-0">
            <TabList className="relative [&>button]:-mb-[1px]">
              <TabTrigger label={followersLabel} value="followers" />
              <TabTrigger label={followingLabel} value="following" />
              <IconButton
                className="absolute bottom-1 right-0 flex"
                icon={<FontAwesomeIcon icon={faXmark} size="lg" />}
                onClick={closeCancelModal}
              />
            </TabList>
          </div>
        }
        body={body}
      />
    </Tabs>
  );
};
